/* ==========================================================================
   dot7-story.js  v2 — one script for every dot7 product story page.

   The important change: ADD TO CART IS NOW AJAX.
   The old build let the form POST natively, so the browser began navigating
   the instant the button was pressed and the animation never rendered. Every
   buy button now posts to /cart/add.js, which means the page never leaves,
   the progress bar always completes, the cart count updates in place and a
   toast confirms the add. If fetch fails for any reason the form submits
   normally, so the customer can still buy.

     [data-d7s-root]     scroll reveal + press feedback + cart
     [data-d7s-gallery]  click / arrows / keyboard / swipe
     [data-d7s-scroll]   horizontal card carousel with dots
     [data-d7s-tabs]     pill tabs
     [data-d7s-race]     two-bar comparison, any unit
     [data-d7s-wave]     dual-driver canvas
     [data-d7s-counter]  count-up number
     [data-d7s-dock]     permanent sticky buy bar
     [data-d7s-qty]      quantity stepper
   ========================================================================== */
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HAS_IO = 'IntersectionObserver' in window;
  function each(l, f) { Array.prototype.forEach.call(l, f); }
  function num(el, a, d) { var v = parseFloat(el.getAttribute(a)); return isNaN(v) ? d : v; }

  /* ---------------- reveal ---------------- */
  function initReveal(root) {
    var items = root.querySelectorAll('.d7s-rv, .d7s-lines');
    if (!items.length) return;
    if (REDUCE || !HAS_IO) { each(items, function (e) { e.classList.add('is-in'); }); return; }
    var io = new IntersectionObserver(function (es) {
      each(es, function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.1 });
    each(items, function (e) { io.observe(e); });
  }

  /* ---------------- toast ---------------- */
  var toastEl = null, toastTimer = 0;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'd7s-toast';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;   /* textContent, not innerHTML — nothing here needs markup */
    toastEl.classList.add('is-on');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { toastEl.classList.remove('is-on'); }, 4000);
  }

  /* ---------------- cart count in the theme header ---------------- */
  function refreshCartCount() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        each(document.querySelectorAll('.m-cart-count, [data-cart-count], .cart-count-bubble span'), function (el) {
          el.textContent = cart.item_count;
          el.classList.remove('m:hidden');
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: cart }));
        document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
      })
      .catch(function () {});
  }

  /* ---------------- press feedback + AJAX add to cart ---------------- */
  function initCart(root) {
    root.addEventListener('pointerdown', function (e) {
      var btn = e.target.closest ? e.target.closest('.d7s-btn') : null;
      if (!btn || REDUCE) return;
      var r = btn.getBoundingClientRect();
      btn.style.setProperty('--d7-rx', (e.clientX - r.left) + 'px');
      btn.style.setProperty('--d7-ry', (e.clientY - r.top) + 'px');
      btn.classList.remove('is-rippling');
      void btn.offsetWidth;
      btn.classList.add('is-rippling');
    }, true);

    root.addEventListener('animationend', function (e) {
      if (e.animationName === 'd7sRipple' && e.target.classList) e.target.classList.remove('is-rippling');
    }, true);

    /* press feedback fires immediately, before any cart app can react */
    function beginAdding(btn) {
      if (!btn || btn.classList.contains('is-loading') || btn.classList.contains('is-done')) return null;
      var label = btn.querySelector('.d7s-btn__t');
      var original = btn.getAttribute('data-label-original') || (label ? label.textContent : '');
      btn.setAttribute('data-label-original', original);
      btn.classList.add('is-loading');
      if (label) label.textContent = btn.getAttribute('data-adding') || 'Adding to basket';

      /* self-completing: runs to the end even if another script swallows the
         submit event, so the customer always gets a finished state */
      window.clearTimeout(btn._d7sT1); window.clearTimeout(btn._d7sT2);
      btn._d7sT1 = window.setTimeout(function () {
        btn.classList.remove('is-loading');
        btn.classList.add('is-done');
        if (label) label.textContent = btn.getAttribute('data-added') || 'Added to basket';
        btn._d7sT2 = window.setTimeout(function () {
          btn.classList.remove('is-done');
          delete btn.dataset.d7sSubmitted;
          if (label) label.textContent = original;
        }, 1500);
      }, 1050);

      return { label: label, original: original, t0: Date.now() };
    }

    each(root.querySelectorAll('form[action*="/cart/add"]'), function (form) {
      var btnEl = form.querySelector('.d7s-btn');
      if (!btnEl) return;

      btnEl.addEventListener('pointerdown', function () { beginAdding(btnEl); });
      btnEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') beginAdding(btnEl);
      });

      /* IMPORTANT — we do NOT preventDefault and we do NOT post to
         /cart/add.js ourselves.

         Cart by Breeze is an app embed (config/settings_data.json ->
         breeze-cart, enabled) and it listens for this very submit to perform
         the add. No theme file handles /cart/add at all. If we also posted,
         the item would be added twice; if Breeze stopped our listener after we
         had already called preventDefault, nothing would be added. Which one
         wins depends on script load order, which a theme cannot guarantee.

         So the cart app owns the add — or, with no app, the browser's native
         POST does, which still works. We own only the animation, which is why
         it starts on pointerdown and completes on its own timer. */
      form.addEventListener('submit', function () {
        beginAdding(btnEl);
        window.setTimeout(function () {
          /* still on the page => an app handled it without navigating */
          refreshCartCount();
          toast('Added to cart &nbsp;<a href="/cart">View cart</a>');
        }, 1100);
      });
    });
  }


  /* ---------------- gallery ----------------
     Every image is already in the DOM. Switching is a class toggle and
     nothing else — no network request, no timer, no waiting. The previous
     build fetched the full-size file on every click, which is why it felt
     like the site had frozen. */
  function initGallery(box) {
    var slides = box.querySelectorAll('[data-d7s-slide]');
    var thumbs = box.querySelectorAll('.d7s-thumb');
    var count = box.querySelector('[data-d7s-count]');
    var frame = box.querySelector('[data-d7s-stage]');
    if (slides.length < 2) return;
    var idx = 0;

    function show(i) {
      var n = (i + slides.length) % slides.length;
      if (n === idx) return;
      idx = n;
      each(slides, function (s, k) { s.classList.toggle('is-on', k === idx); });
      each(thumbs, function (t, k) {
        t.classList.toggle('is-active', k === idx);
        t.setAttribute('aria-current', k === idx ? 'true' : 'false');
      });
      if (count) count.textContent = (idx + 1) + ' / ' + slides.length;
      var act = thumbs[idx];
      if (act && act.scrollIntoView) {
        act.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: REDUCE ? 'auto' : 'smooth' });
      }
      /* nudge the neighbours out of lazy so the NEXT click is instant too */
      [slides[(idx + 1) % slides.length], slides[(idx - 1 + slides.length) % slides.length]]
        .forEach(function (s) { if (s && s.loading === 'lazy') s.loading = 'eager'; });
    }

    each(thumbs, function (t, n) {
      t.addEventListener('click', function () { show(n); });
    });
    var p = box.querySelector('[data-d7s-prev]'), nx = box.querySelector('[data-d7s-next]');
    if (p) p.addEventListener('click', function () { show(idx - 1); });
    if (nx) nx.addEventListener('click', function () { show(idx + 1); });
    if (!frame) return;

    frame.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(idx - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(idx + 1); }
    });

    var sx = 0, sy = 0, tracking = false;
    frame.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
    }, { passive: true });
    frame.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(dx < 0 ? idx + 1 : idx - 1);
    }, { passive: true });
  }

  /* ---------------- card carousel + dots ---------------- */
  function initScroller(rail) {
    var cards = rail.querySelectorAll('.d7s-card');
    if (cards.length < 2) return;
    /* This runs again on every shopify:section:load, and the customizer fires
       that on each setting change. Without clearing, a fresh dot row was
       inserted each time and they stacked up — four edits, four rows of dots.
       Remove any row this rail already owns, and mark the rail as done so a
       double call in the same pass cannot duplicate either. */
    var old = rail.parentNode.querySelectorAll(':scope > .d7s-dots');
    for (var k = 0; k < old.length; k++) old[k].parentNode.removeChild(old[k]);
    var dots = document.createElement('div');
    dots.className = 'd7s-dots';
    each(cards, function (c, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', 'Go to item ' + (i + 1));
      if (i === 0) b.className = 'is-on';
      b.addEventListener('click', function () {
        /* centre the card, clamped to the rail's real scroll range — the last
           card can never reach the centre, which is why its dot never lit */
        var target = (c.offsetLeft - rail.offsetLeft) - (rail.clientWidth - c.offsetWidth) / 2;
        var max = rail.scrollWidth - rail.clientWidth;
        rail.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: REDUCE ? 'auto' : 'smooth' });
      });
      dots.appendChild(b);
    });
    rail.parentNode.insertBefore(dots, rail.nextSibling);

    var ticking = false;
    rail.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var mid = rail.scrollLeft + rail.clientWidth / 2, best = 0, bd = Infinity;
        each(cards, function (c, i) {
          var d = Math.abs((c.offsetLeft - rail.offsetLeft) + c.offsetWidth / 2 - mid);
          if (d < bd) { bd = d; best = i; }
        });
        /* at either end, the end card is the active one regardless of centre */
        if (rail.scrollLeft <= 1) best = 0;
        if (rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1) best = cards.length - 1;
        each(dots.children, function (d, i) { d.classList.toggle('is-on', i === best); });
        ticking = false;
      });
    }, { passive: true });
  }

  /* ---------------- pill tabs ---------------- */
  function initTabs(box) {
    var pills = box.querySelectorAll('.d7s-pill');
    var panels = box.querySelectorAll('.d7s-panel');
    if (!pills.length) return;
    each(pills, function (pill, i) {
      pill.addEventListener('click', function () {
        each(pills, function (p, k) { p.classList.toggle('is-on', k === i); p.setAttribute('aria-selected', k === i ? 'true' : 'false'); });
        each(panels, function (p, k) { p.classList.toggle('is-on', k === i); });
      });
    });
  }

  /* ---------------- race ---------------- */
  function initRace(box) {
    var fF = box.querySelector('[data-d7s-fill="fast"]'), fS = box.querySelector('[data-d7s-fill="slow"]');
    var vF = box.querySelector('[data-d7s-val="fast"]'), vS = box.querySelector('[data-d7s-val="slow"]');
    if (!fF || !fS) return;
    var unit = box.getAttribute('data-unit') || '', prefix = box.getAttribute('data-prefix') || '';
    var fast = num(box, 'data-fast', 100), slow = num(box, 'data-slow', 50);
    var scale = num(box, 'data-scale', Math.max(fast, slow)) || 1;
    var cycle = num(box, 'data-cycle', 2600), hold = num(box, 'data-hold', 1600);
    var fe = Math.min(1, fast / scale), se = Math.min(1, slow / scale), lead = Math.max(fe, se) || 1;
    var lf = null, ls = null;
    function paint(kf, ks) {
      fF.style.transform = 'scaleX(' + kf + ')';
      fS.style.transform = 'scaleX(' + ks + ')';
      if (vF) { var a = Math.round(kf * scale); if (a !== lf) { vF.textContent = prefix + a + unit; lf = a; } }
      if (vS) { var b = Math.round(ks * scale); if (b !== ls) { vS.textContent = prefix + b + unit; ls = b; } }
    }
    if (REDUCE || !HAS_IO) { paint(fe, se); return; }
    var running = false, raf = 0, start = 0;
    function tick(t) {
      if (!running) return;
      if (!start) start = t;
      var el = t - start;
      if (el >= cycle + hold) { start = t; el = 0; }
      var travelled = Math.min(1, el / cycle) * lead;
      paint(Math.min(fe, travelled), Math.min(se, travelled));
      raf = window.requestAnimationFrame(tick);
    }
    new IntersectionObserver(function (es) {
      var v = es[0].isIntersecting;
      if (v && !running) { running = true; start = 0; raf = window.requestAnimationFrame(tick); }
      else if (!v && running) { running = false; window.cancelAnimationFrame(raf); }
    }, { threshold: 0.3 }).observe(box);
  }

  /* ---------------- waveform ---------------- */
  function initWave(canvas) {
    if (!canvas.getContext) return;
    var ctx = canvas.getContext('2d'), W = 0, H = 0, DPR = Math.min(2, window.devicePixelRatio || 1);
    function size() {
      var r = canvas.getBoundingClientRect();
      W = Math.max(1, Math.round(r.width)); H = Math.max(1, Math.round(r.height));
      canvas.width = W * DPR; canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    }
    function stroke(c, w, amp, f, sp, ph, t) {
      ctx.beginPath(); ctx.strokeStyle = c; ctx.lineWidth = w;
      var mid = H / 2, n = Math.ceil(W / 3);
      for (var i = 0; i <= n; i++) {
        var x = i * 3, y = mid + Math.sin(x * f + t * sp + ph) * amp;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    function draw(t) {
      ctx.clearRect(0, 0, W, H);
      stroke('#d5d5d5', 5, H * 0.30, 0.010, 0.0016, 0, t);
      stroke('#8f8f8f', 1.5, H * 0.10, 0.055, 0.0042, 1.2, t);
      ctx.beginPath(); ctx.strokeStyle = '#111'; ctx.lineWidth = 2;
      var mid = H / 2, n = Math.ceil(W / 3);
      for (var i = 0; i <= n; i++) {
        var x = i * 3;
        var y = mid + Math.sin(x * 0.010 + t * 0.0016) * H * 0.30 * 0.7
                    + Math.sin(x * 0.055 + t * 0.0042 + 1.2) * H * 0.10 * 0.7;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    size();
    if (REDUCE || !HAS_IO) { draw(900); window.addEventListener('resize', function () { size(); draw(900); }); return; }
    var running = false, raf = 0;
    function tick(t) { if (!running) return; draw(t); raf = window.requestAnimationFrame(tick); }
    window.addEventListener('resize', size);
    new IntersectionObserver(function (es) {
      var v = es[0].isIntersecting;
      if (v && !running) { running = true; raf = window.requestAnimationFrame(tick); }
      else if (!v && running) { running = false; window.cancelAnimationFrame(raf); }
    }, { threshold: 0.2 }).observe(canvas);
  }

  /* ---------------- counter ---------------- */
  function initCounter(el) {
    var target = num(el, 'data-d7s-counter', 0), dur = num(el, 'data-duration', 1400);
    function fmt(v) { return v.toLocaleString('en-IN'); }
    function run() {
      if (REDUCE) { el.textContent = fmt(target); return; }
      var t0 = window.performance.now();
      (function step(t) {
        var k = Math.min(1, (t - t0) / dur);
        el.textContent = fmt(Math.round(target * (1 - Math.pow(1 - k, 3))));
        if (k < 1) window.requestAnimationFrame(step);
      })(t0);
    }
    if (!HAS_IO) { run(); return; }
    new IntersectionObserver(function (es, obs) { if (es[0].isIntersecting) { run(); obs.disconnect(); } }, { threshold: 0.4 }).observe(el);
  }

  /* ---------------- quantity ---------------- */
  function initQty(box) {
    var input = box.querySelector('[data-d7s-qty-input]');
    if (!input) return;
    var min = parseInt(input.getAttribute('min') || '1', 10);
    function step(by) {
      var v = parseInt(input.value, 10);
      if (isNaN(v)) v = min;
      v += by; if (v < min) v = min;
      input.value = v;
    }
    var m = box.querySelector('[data-d7s-minus]'), p = box.querySelector('[data-d7s-plus]');
    if (m) m.addEventListener('click', function () { step(-1); });
    if (p) p.addEventListener('click', function () { step(1); });
    input.addEventListener('blur', function () {
      var v = parseInt(input.value, 10);
      input.value = (isNaN(v) || v < min) ? min : v;
    });
  }

  /* ---------------- sticky dock: permanent once the hero is gone ------- */
  function initDock(dock) {
    var after = document.querySelector(dock.getAttribute('data-after') || '');
    if (!after || !HAS_IO) { dock.classList.add('is-on'); return; }
    new IntersectionObserver(function (e) {
      dock.classList.toggle('is-on', !e[0].isIntersecting);
    }, { threshold: 0 }).observe(after);
  }

  /* ---------------- app slots collapse if the app never renders -------- */
  function initSlots(root) {
    each(root.querySelectorAll('.d7s-slot'), function (slot) {
      var settled = false;
      function check() {
        if (settled) return;
        if (slot.querySelector('iframe, img, .jdgm-rev, canvas, video') ||
            slot.textContent.replace(/\s+/g, '').length > 0) { settled = true; slot.classList.remove('is-empty'); }
      }
      check(); if (settled) return;
      var mo = ('MutationObserver' in window) ? new MutationObserver(check) : null;
      if (mo) mo.observe(slot, { childList: true, subtree: true });
      window.setTimeout(function () { check(); if (!settled) slot.classList.add('is-empty'); if (mo) mo.disconnect(); }, 6000);
    });
  }


  /* ---------------- live rating from the Judge.me widget ----------------
     Judge.me writes the real numbers onto its widget element as
     data-average-rating / data-number-of-reviews once it hydrates. We mirror
     them into the hero and buy panel, so the star line is never typed text
     that can drift from the actual reviews. Clicking scrolls to the widget. */
  function initRating(root) {
    var links = root.querySelectorAll('[data-d7s-rating]');
    if (!links.length) return;

    each(links, function (a) {
      a.addEventListener('click', function (e) {
        var target = document.querySelector(a.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' });
      });
    });

    function paint() {
      var w = document.querySelector('.jdgm-rev-widg, .jdgm-widget[data-average-rating]');
      if (!w) return false;
      var avg = parseFloat(w.getAttribute('data-average-rating'));
      var num = parseInt(w.getAttribute('data-number-of-reviews'), 10);
      if (isNaN(avg) || isNaN(num) || num < 1) return false;

      var rounded = Math.round(avg * 2) / 2;
      var stars = '';
      for (var i = 1; i <= 5; i++) {
        stars += (i <= rounded) ? '\u2605' : (i - 0.5 === rounded ? '\u2605' : '\u2606');
      }
      each(links, function (a) {
        var st = a.querySelector('[data-d7s-rating-stars]');
        var tx = a.querySelector('[data-d7s-rating-text]');
        if (st) st.textContent = stars;
        if (tx) tx.textContent = avg.toFixed(1) + ' \u00b7 ' + num + (num === 1 ? ' review' : ' reviews');
        a.classList.add('is-live');
      });
      return true;
    }

    if (paint()) return;
    if (!('MutationObserver' in window)) { window.setTimeout(paint, 2500); return; }
    var mo = new MutationObserver(function () { if (paint()) mo.disconnect(); });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-average-rating'] });
    window.setTimeout(function () { mo.disconnect(); }, 12000);
  }


  /* ---------------- video: play while visible, pause when not -------------
     autoplay+muted+playsinline satisfies mobile autoplay policies; the
     observer keeps offscreen video from burning battery and bandwidth. */
  function initVideo(v) {
    v.muted = true;                 /* required for programmatic play() */
    v.setAttribute('muted', '');
    if (REDUCE) { v.removeAttribute('autoplay'); return; }
    function tryPlay() { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
    if (!HAS_IO) { tryPlay(); return; }
    new IntersectionObserver(function (es) {
      if (es[0].isIntersecting) tryPlay(); else v.pause();
    }, { threshold: 0.25 }).observe(v);
  }


  /* ---------------- content pages: anchor the h2s for the contents list ----
     The page body is merchant HTML, so the headings have no ids. We add them
     in the same order the Liquid built the list, then smooth-scroll. */
  function initProse(prose) {
    var hs = prose.querySelectorAll('h2');
    each(hs, function (h, i) { if (!h.id) h.id = 'd7s-h' + (i + 1); });

    var toc = document.querySelector('.d7s-toc');
    if (!toc) return;
    each(toc.querySelectorAll('a[href^="#d7s-h"]'), function (a) {
      a.addEventListener('click', function (e) {
        var t = document.getElementById(a.getAttribute('href').slice(1));
        if (!t) return;
        e.preventDefault();
        t.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', a.getAttribute('href'));
      });
    });
  }


  /* ---------------- parallax drift ----------------
     A single rAF loop, only while an element is on screen, writing one
     transform. No layout, no scroll listener storm. This is the difference
     between a page that feels flat and one that feels made. */
  function initParallax(root) {
    if (REDUCE || !HAS_IO) return;
    var items = [];
    each(root.querySelectorAll('[data-d7s-parallax]'), function (el) {
      items.push({ el: el, k: parseFloat(el.getAttribute('data-d7s-parallax')) || 0.06, on: false });
    });
    if (!items.length) return;

    var io = new IntersectionObserver(function (es) {
      each(es, function (e) {
        for (var i = 0; i < items.length; i++) {
          if (items[i].el === e.target) { items[i].on = e.isIntersecting; break; }
        }
      });
      kick();
    }, { rootMargin: '10% 0px' });
    each(items, function (it) { io.observe(it.el); });

    var raf = 0, running = false;
    function frame() {
      var any = false, vh = window.innerHeight || 1;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it.on) continue;
        any = true;
        var r = it.el.getBoundingClientRect();
        /* -1 at the bottom of the screen, +1 at the top */
        var mid = (r.top + r.height / 2 - vh / 2) / vh;
        var shift = -(mid * it.k * 100);
        it.el.style.transform = 'scale(1.06) translate3d(0,' + shift.toFixed(2) + 'px,0)';
      }
      if (any) { raf = window.requestAnimationFrame(frame); } else { running = false; }
    }
    function kick() { if (!running) { running = true; raf = window.requestAnimationFrame(frame); } }
    kick();
  }


  /* Line-by-line splitting was removed on purpose. It measured word
     positions and rebuilt the heading, which turned any narrow container into
     one word per line — exactly what happened on the statement bands. The
     plain reveal below is indistinguishable when the layout is correct and
     cannot break the text. */

  /* ---------------- marquee: duplicate the track so the loop is seamless -- */
  function initMarquee(box) {
    var track = box.querySelector('.d7s-marquee-track');
    if (!track || track.children.length < 2) {
      if (track) track.appendChild(track.firstElementChild.cloneNode(true));
    }
    if (REDUCE) track.style.animation = 'none';
    if (!HAS_IO) return;
    new IntersectionObserver(function (es) {
      track.style.animationPlayState = es[0].isIntersecting ? 'running' : 'paused';
    }, { threshold: 0 }).observe(box);
  }

  /* The clip-path wipe was removed. It hid product images until JavaScript
     revealed them, which meant any failure showed a customer an empty tile.
     No animation is worth that on a photograph of the thing you are selling. */

  /* ---------------- animated hero background ----------------
     The animation is entirely CSS. This only stops it when the hero scrolls
     away, so a long page never pays for motion nobody can see. */
  function initBg(el) {
    if (!HAS_IO) return;
    new IntersectionObserver(function (es) {
      el.classList.toggle('is-paused', !es[0].isIntersecting);
    }, { threshold: 0 }).observe(el);
  }

  /* Arm the reveal animations. Until this class exists the CSS keeps every
     element visible, so a blocked or broken script can never leave a blank
     page — it just means no animation. */
  try { document.documentElement.classList.add('d7s-js'); } catch (e) {}

  /* ---------------- boot ---------------- */
  function boot(scope) {
    var r = scope || document;
    if (document.querySelector('[data-d7s-root]')) document.documentElement.classList.add('d7s-page');
    each(r.querySelectorAll('[data-d7s-root]'), initReveal);
    initCart(document);   /* covers the sticky dock, which sits outside .d7s */
    each(r.querySelectorAll('[data-d7s-root]'), initSlots);
    each(r.querySelectorAll('[data-d7s-gallery]'), initGallery);
    each(r.querySelectorAll('[data-d7s-scroll]'), initScroller);
    each(r.querySelectorAll('[data-d7s-tabs]'), initTabs);
    each(r.querySelectorAll('[data-d7s-race]'), initRace);
    each(r.querySelectorAll('[data-d7s-wave]'), initWave);
    each(r.querySelectorAll('[data-d7s-counter]'), initCounter);
    each(r.querySelectorAll('[data-d7s-qty]'), initQty);
    each(r.querySelectorAll('[data-d7s-dock]'), initDock);
    initRating(document);
    each(r.querySelectorAll('[data-d7s-video]'), initVideo);
    each(r.querySelectorAll('[data-d7s-prose]'), initProse);
    initParallax(r);
    each(r.querySelectorAll('[data-d7s-bg]'), initBg);
    each(r.querySelectorAll('[data-d7s-marquee]'), initMarquee);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  window.addEventListener('pageshow', function () {
    each(document.querySelectorAll('.d7s-btn'), function (b) { b.classList.remove('is-loading', 'is-done', 'is-rippling'); });
  });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  window.Dot7Story = { boot: boot };
})();

/* Thumbnail strip affordance. The strip always scrolled; nothing said so.
   Arrows and edge fades are shown only when it genuinely overflows, and the
   arrows disable at each end. Passive scroll listener, and the observer is
   only attached where a strip exists, so pages without a gallery pay nothing. */
(function () {
  function d7sThumbs(root) {
  var wraps = (root || document).querySelectorAll('[data-d7s-thumbwrap]');
  if (!wraps.length) return;

  Array.prototype.forEach.call(wraps, function (wrap) {
    var track = wrap.querySelector('[data-d7s-thumbs]');
    var prev = wrap.querySelector('[data-d7s-thumbprev]');
    var next = wrap.querySelector('[data-d7s-thumbnext]');
    if (!track || !prev || !next) return;

    function sync() {
      var max = track.scrollWidth - track.clientWidth;
      var over = max > 4;
      wrap.classList.toggle('is-over', over);
      wrap.classList.toggle('at-start', track.scrollLeft <= 4);
      wrap.classList.toggle('at-end', track.scrollLeft >= max - 4);
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= max - 4;
    }
    function step(dir) {
      track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
    }
    prev.addEventListener('click', function () { step(-1); });
    next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', sync, { passive: true });

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(sync).observe(track);
    } else {
      window.addEventListener('resize', sync, { passive: true });
    }
    sync();
  });
}
  d7sThumbs(document);
  document.addEventListener('shopify:section:load', function (e) { d7sThumbs(e.target); });
})();
